import React, { useCallback, useState, useEffect } from "react";
import { find, propEq, path, prop } from "ramda";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Stack, Link, FormLabel, Text } from "@chakra-ui/react";
import { useFormikContext } from "formik";
import { SearchableSelectField, TextFieldGroup as TextField } from "components/Form";
import {
  countryHasStates,
  toRegionOptions,
  toStateOptions,
  toStateName,
  localizedRegionsFactory,
} from "utils/regions";
import PlacesAutocompleteAddressTextField from "components/Form/PlacesAutocompleteAddressTextField/PlacesAutocompleteAddressTextFieldV2";

const DEFAULT_ADDRESS_FORM_NAMES = {
  street: "street",
  street2: "street2",
  zip: "zip",
  city: "city",
  state: "state",
  country: "country",
  googlePlacesAutocomplete: "googlePlacesAutocomplete",
};

/**
 * @function AddressWithPlacesAutocomplete A standard set of Formik field inputs paired
 * with Google Places Autocomplete (<PlacesAutocompleteAddressTextField />).
 * @param {object} props
 * @param {string} [props.label=Address] Label of first input field
 * @param {function} [props.onPlaceChange] Optional callback for place_change event
 * @returns {JSX.Element}
 */
export default function AddressWithPlacesAutocomplete({
  label = "Address",
  country: countryProps,
  onPlaceChange,
  names = DEFAULT_ADDRESS_FORM_NAMES,
}) {
  const { formatMessage } = useIntl();
  const regions = localizedRegionsFactory(formatMessage);
  const [autocompleteEnabled, setAutocompleteEnabled] = useState(true);
  const { values, setFieldTouched, setFieldValue, validateForm } = useFormikContext();

  const streetValues = path(names.street.split("."), values);
  const googlePlacesAutocompleteValues = path(names.googlePlacesAutocomplete.split("."), values);
  const countryValue = path(names.country.split("."), values);
  const selectedCountry = find(propEq("ISO", countryValue))(regions);
  const selectedCountryHasStates = countryHasStates(countryValue);

  const onClickManualMode = useCallback(() => {
    if (!streetValues) {
      setFieldValue(names.street, googlePlacesAutocompleteValues);
    }
    setAutocompleteEnabled(false);
  }, [names.street, setFieldValue, googlePlacesAutocompleteValues, streetValues]);

  // Force form validation to revalidate after pause, avoiding a race condition
  const forceFormValidation = useCallback(() => {
    setTimeout(validateForm, 500); // for some reason, we need a longer pause here than 1 tic, if there's an issue with the submit button not being enabled when it should be, here is a likely spot to play with
  }, [validateForm]);

  const onUserSelection = useCallback(
    ({ formatted_address, address_components, address_components_dict, tilia_address }) => {
      setFieldValue(names.googlePlacesAutocomplete, formatted_address || "");
      setFieldValue(names.street, tilia_address.street || "");
      setFieldValue(names.street2, tilia_address.street2 || "");
      setFieldValue(names.zip, tilia_address.zip || "");
      setFieldValue(names.city, tilia_address.city || "");
      setFieldValue(names.state, tilia_address.state || "");
      setFieldValue(names.country, tilia_address.country || "");

      setFieldTouched(names.street, true);
      setFieldTouched(names.zip, true);
      setFieldTouched(names.city, true);
      setFieldTouched(names.state, true);
      setFieldTouched(names.country, true);
      forceFormValidation();

      setAutocompleteEnabled(false); // This switches the form to manual mode no matter if we have all the info we need or not, effectively making address autocomplete a one-shot UI.

      if (typeof onPlaceChange === "function") {
        onPlaceChange({
          formatted_address,
          address_components,
          address_components_dict,
          tilia_address,
        });
      }
    },
    [onPlaceChange, setFieldTouched, setFieldValue, names, forceFormValidation]
  );

  useEffect(() => {
    setFieldValue(names.country, countryProps);
  }, [setFieldValue, countryProps, names.country]);

  return (
    <>
      {autocompleteEnabled ? (
        <Stack spacing={5}>
          <PlacesAutocompleteAddressTextField
            label={label}
            placeholder={formatMessage({
              defaultMessage: "Enter Address",
              description: "input placeholder",
            })}
            name={names.googlePlacesAutocomplete}
            onUserSelection={onUserSelection}
            country={countryProps}
          />
          {countryProps && (
            <Stack spacing={1}>
              <FormLabel requiredIndicator={false}>
                <FormattedMessage defaultMessage="Country of residence" description="field label" />
              </FormLabel>
              <Text color="tertiaryText">{prop("Name", selectedCountry)}</Text>
            </Stack>
          )}
          <Link
            data-testid="address-autocomplete-manual-mode-button"
            color="integrator.500"
            onClick={onClickManualMode}
          >
            <FormattedMessage
              defaultMessage="Enter address manually"
              description="Link/button text"
            />
          </Link>
        </Stack>
      ) : (
        <Stack spacing={3} direction="column" data-testid="address-manual-form">
          <TextField
            label={label}
            placeholder={formatMessage({
              defaultMessage: "Street address",
              description: "input placeholder",
            })}
            name={names.street}
          />
          <TextField
            name={names.street2}
            placeholder={formatMessage({
              defaultMessage: "Apt., ste., bldg.",
              description: "input placeholder",
            })}
            isOptional
          />

          <Stack spacing={3} direction="row">
            <Box flex={1}>
              <SearchableSelectField
                placeholder={formatMessage({
                  defaultMessage: "Select...",
                  description: "select placeholder",
                })}
                label={formatMessage({
                  defaultMessage: "Country/Region",
                  description: "input label",
                })}
                isReadOnly={Boolean(countryProps)}
                name={names.country}
                options={toRegionOptions(regions)}
              />
            </Box>
            <Box flex={1}>
              {selectedCountryHasStates ? (
                <SearchableSelectField
                  placeholder={formatMessage({
                    defaultMessage: "Select...",
                    description: "select placeholder",
                  })}
                  label={formatMessage({
                    defaultMessage: "State/Province",
                    description: "input label",
                  })}
                  name={names.state}
                  options={toStateOptions(selectedCountry.States)}
                  valueModifier={(v) => toStateName(countryValue, v)}
                />
              ) : (
                <TextField
                  label={formatMessage({
                    defaultMessage: "State/Province",
                    description: "input label",
                  })}
                  name={names.state}
                />
              )}
            </Box>
          </Stack>

          <Stack spacing={3} direction="row">
            <Box flex={1}>
              <TextField
                label={formatMessage({
                  defaultMessage: "City",
                  description: "input label",
                })}
                name={names.city}
              />
            </Box>
            <Box flex={1}>
              <TextField
                label={formatMessage({
                  defaultMessage: "Postal Code",
                  description: "input label",
                })}
                name={names.zip}
                maxLength={10}
              />
            </Box>
          </Stack>
        </Stack>
      )}
    </>
  );
}
